"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import type { EChartsOption } from "echarts";
import type { EChartsType } from "echarts";
import { Download, Lock } from "lucide-react";
import { ChartLoader } from "./ChartLoader";
import { ChartError } from "./ChartError";
import { ChartEmpty } from "./ChartEmpty";
import { CHART_HEIGHT_DESKTOP, RANGE_OPTIONS, isRangeLocked, type RangeOption } from "./constants";

// ECharts is ~1.3 MB raw / ~350 KB gz — by far the heaviest thing this app
// ships. A static import puts it in the route's first-load JS even though
// nothing renders until a fetch resolves inside an IntersectionObserver
// (rootMargin 200 px), so the bytes always arrived long before they were
// needed and competed with the LCP image for bandwidth.
//
// `ssr: false` changes no rendered output: this file is already "use client",
// and the <EChart> branch below is unreachable until `isLoading` flips false,
// which only happens after a client-side fetch. The chunk therefore downloads
// in parallel with that fetch instead of ahead of it.
//
// The `loading` state reuses ChartLoader so a slow chunk looks identical to a
// slow fetch — the container has a fixed height either way, so no layout shift.
const EChart = dynamic(() => import("./EChart").then((m) => m.EChart), {
  ssr: false,
  loading: () => <ChartLoader />,
});
import { useUser } from "@/features/auth/hooks/useUser";
import { useAuthModal } from "@/features/auth/hooks/useAuthModal";
import { usePricingModal } from "@/features/auth/hooks/usePricingModal";
import type { ChartFestivalMarker } from "./chartEventMarkers";
import { capture } from "@/lib/analytics/posthog";
import { useLockImpression } from "@/lib/analytics/useLockImpression";
import { isPaidPlan } from "@/lib/auth/plans";
import { hasPaidAccess } from "@/lib/auth/access";
import { chartOptionsToCsv, downloadCsv } from "./chartCsv";
import { mayHaveOlderHistory, type HistoryExtent } from "./historyExtent";

type ChartContainerProps = {
  chartOptions: EChartsOption;
  isLoading: boolean;
  error: string | null;
  hasData: boolean;
  height?: number;
  primaryColor?: string;
  /** When true, shows a banner prompting the user to unlock more data */
  dataClamped?: boolean;
  /** Pass range options to auto-detect if any ranges are locked for the user (defaults to RANGE_OPTIONS) */
  rangeOptions?: RangeOption[];
  /** Which chart this container renders — used to label paywall events. */
  chart?: string;
  /**
   * What the chart loaded and where its data starts. With it, the history
   * banner shows only when older data is plausible (`mayHaveOlderHistory`) —
   * not on a game tracked for four days. Absent → the banner follows the plan
   * alone, as before.
   */
  historyExtent?: HistoryExtent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events?: Record<string, ((params?: any) => void) | undefined>;
  onChartReady?: (chart: EChartsType) => void;
  festivalMarkers?: ChartFestivalMarker[];
  /** Optional action rendered on the same row as the unlock banner (e.g. "All regions") */
  footerAction?: React.ReactNode;
  /**
   * What the CSV button exports, when the drawn chart isn't the data: the
   * price chart's "even spacing" mode plots on a category axis, and the
   * reviews chart draws negative reviews below zero. Defaults to chartOptions.
   */
  csvOptions?: EChartsOption;
};

export function ChartContainer({
  chartOptions,
  isLoading,
  error,
  hasData,
  height,
  primaryColor = "#66c0f4",
  dataClamped,
  rangeOptions,
  chart = "unknown",
  historyExtent,
  events,
  onChartReady,
  festivalMarkers,
  footerAction,
  csvOptions,
}: ChartContainerProps) {
  const resolvedHeight = height ?? CHART_HEIGHT_DESKTOP;
  const { plan, user, serverEmail, loading } = useUser();

  const urlByName = useMemo(() => {
    if (!festivalMarkers?.length) return null;
    const map = new Map<string, string>();
    for (const f of festivalMarkers) {
      if (f.url) map.set(f.name, f.url);
    }
    return map;
  }, [festivalMarkers]);

  const mergedEvents = useMemo(() => {
    if (!urlByName?.size) return events;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClick = (params: any) => {
      if (params?.seriesName === "_festivals" && params?.name) {
        const url = params.name.split("||")[2];
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      }
    };
    return { ...events, click: handleClick };
  }, [events, urlByName]);
  const { open: openAuth } = useAuthModal();
  const { open: openPricing } = usePricingModal();

  // Show upgrade banner when explicitly clamped OR when there are locked ranges
  const opts = rangeOptions ?? RANGE_OPTIONS;
  const hasLockedRanges = opts.some((opt) => isRangeLocked(opt, plan));
  // Any paid tier has full history — Studio included (this read `!== "pro"`,
  // which showed Studio subscribers an "Upgrade to Pro" banner).
  const olderHistoryPlausible = historyExtent ? mayHaveOlderHistory(historyExtent, plan) : true;
  const showBanner =
    (dataClamped || hasLockedRanges) && !isPaidPlan(plan) && olderHistoryPlausible;
  // A signed-in user reads "anonymous" until their plan loads — the banner
  // briefly says "Sign in" to them; only a settled plan counts as an impression.
  const planSettled = !loading && !(user && plan === "anonymous");
  const bannerImpressionRef = useLockImpression(`chart_history:${chart}`, {
    required: plan === "anonymous" ? "account" : "pro",
    plan,
    enabled: showBanner && planSettled,
  });

  return (
    <div>
      <div
        className="relative"
        style={{ height: resolvedHeight, minHeight: resolvedHeight }}
      >
        {isLoading && <ChartLoader primaryColor={primaryColor} />}

        {error && <ChartError error={error} />}

        {!isLoading && !error && !hasData && <ChartEmpty />}

        {!isLoading && !error && hasData && (
          <EChart
            option={chartOptions}
            style={{ height: resolvedHeight, width: "100%" }}
            events={mergedEvents}
            onChartReady={onChartReady}
          />
        )}
      </div>

      {!isLoading && !error && hasData && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {showBanner && (
            <button
              ref={bannerImpressionRef}
              type="button"
              onClick={() => {
                capture("paywall hit", {
                  feature: `chart_history_banner:${chart}`,
                  plan,
                });
                if (plan === "anonymous") {
                  openAuth();
                } else {
                  openPricing("chart_history_banner");
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1b2838] border border-[#2a475e] text-[#8f98a0] text-xs hover:text-[#c7d5e0] hover:border-[#3a556e] transition-colors"
            >
              <Lock className="w-3 h-3" />
              {plan === "anonymous"
                ? "Sign in to see more history"
                : "Upgrade to Pro for full history"}
            </button>
          )}
          {footerAction}
          <button
            type="button"
            data-screenshot-ignore="true"
            onClick={() => {
              // CSV export is a Pro feature. The gate can live here: the chart
              // already holds only what the server's plan clamp sent, so a
              // free user's file would add nothing they can't see.
              if (!hasPaidAccess(plan, user?.email || serverEmail)) {
                capture("paywall hit", { feature: `csv_export:${chart}`, plan });
                if (plan === "anonymous") openAuth();
                else openPricing("csv_export");
                return;
              }
              const csv = chartOptionsToCsv(csvOptions ?? chartOptions);
              if (!csv) return;
              downloadCsv(csv, `steampeek-${chart.replace(/[^a-z0-9_-]+/gi, "-")}-${new Date().toISOString().slice(0, 10)}.csv`);
            }}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1b2838] border border-[#2a475e] text-[#8f98a0] text-xs hover:text-[#c7d5e0] hover:border-[#3a556e] transition-colors"
            title={hasPaidAccess(plan, user?.email || serverEmail) ? "Download this chart's data as CSV" : "CSV export is part of Pro"}
          >
            {hasPaidAccess(plan, user?.email || serverEmail) ? (
              <Download className="w-3 h-3" aria-hidden />
            ) : (
              <Lock className="w-3 h-3" aria-hidden />
            )}
            CSV
          </button>
        </div>
      )}
    </div>
  );
}
